import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LogoImg from "../Images/orra_logo_reszie.png";
import NavUserInfo from "../ProfileComponents/NavUserInfo";
import DocumentationCard from "../ProfileComponents/DocumentationCard";
import { SvgIcon } from "./SvgIcon";
import useUserRole from "../hooks/useUserRole";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase/config";
import SidebarSkeleton from "../Skeleton/SidebarSkeleton";
import { ChevronDown, Lock, Video } from "lucide-react";

const userSideBar = [
  { to: "/", text: "Accueil", icon: "home" },
  { to: "/resume", text: "Reprendre la formation", icon: "resume" },
  { to: "/ranking", text: "Classement", icon: "ranking" },
  { to: "/outils-ia", text: "Outils IA", icon: "AiTools" },
  { to: "/ai-agents", text: "Templates AI", icon: "aiAgents" },
  // { to: "/ai-prompts", text: "Mes Prompts", icon: "aiPrompts" },
  { to: "/reward", text: "Mes récompenses", icon: "reward" },
  {
    to: "/book-sessions",
    text: "Séances de réservation",
    icon: "booksessions",
    requiresOneToOne: true,
  },
  { to: "/settings", text: "Paramètres", icon: "settings" },
  // { to: "/coaching-roadmap", text: "mon coaching", icon: "studentRoadMapOverview" },
];

const adminSidebarGroups = [
  {
    title: "Lead management",
    icon: "home",
    items: [
      { to: "/admin/home", text: "Lead", icon: "home" },
      { to: "/admin/kanban", text: "Kanban", icon: "kanban" },
      { to: "/admin/lead-magnet", text: "Lead Magnet", icon: "leadMagnet" },
      { to: "/admin/track-lead", text: "Track Lead", icon: "trackUser" },
      { to: "/admin/magnets-videos", text: "Orra Discovery", icon: "video" },
    ],
  },
  {
    title: "AI Tools",
    icon: "AiTools",
    items: [
      {
        to: "/admin/add-agent-template",
        text: "Add Agent Template",
        icon: "aiAgents",
      },
      { to: "/admin/ai-prompts-list", text: "Add Prompts", icon: "aiPrompts" },
      { to: "/admin/ai-tools-list", text: "Outils IA", icon: "AiTools" },
    ],
  },
  {
    title: "User Management",
    icon: "users",
    items: [
      { to: "/admin/user-stats", text: "Users Stats", icon: "users" },
      {
        to: "/admin/discounted-users",
        text: "Discounted users",
        icon: "signupLink",
      },
      { to: "/admin/manage-users", text: "Manage Users", icon: "users" },
      { to: "/admin/track-lead", text: "Track Lead", icon: "trackUser" },
      {
        to: "/admin/source-aquisition",
        text: "Source Acquisition",
        icon: "sourceAquisition",
      },
      {
        to: "/admin/masterclass-stats",
        text: "Masterclass Stats",
        icon: "stats",
      },
      {
        to: "/admin/masterclass-users",
        text: "Masterclass Users",
        icon: "users",
      },
    ],
  },
  {
    title: "Finance & Sales",
    icon: "finance",
    items: [
      {
        to: "/admin/product-purchases",
        text: "Product Purchases",
        icon: "productPurchase",
      },
      { to: "/admin/finance", text: "Finance", icon: "finance" },
      { to: "/admin/deposits", text: "Deposits", icon: "finance" },
      {
        to: "/admin/checkout-discount-links",
        text: "Checkout Discount",
        icon: "signupLink",
      },
      {
        to: "/admin/paypal-discount-links",
        text: "Paypal Discount Links",
        icon: "signupLink",
      },
      { to: "/admin/manage-price", text: "Price", icon: "dollar" },
    ],
  },
  {
    title: "Course",
    icon: "module",
    items: [
      { to: "/admin/sections", text: "Sections", icon: "section" },
      { to: "/admin/modules", text: "Modules", icon: "module" },
      { to: "/admin/videos", text: "Videos", icon: "video" },
      { to: "/admin/allquestions", text: "Questions", icon: "quiz" },
      { to: "/admin/quizes", text: "Quizes", icon: "quiz" },
      { to: "/admin/rewards", text: "Rewards", icon: "reward" },
    ],
  },
  {
    title: "Coaching",
    icon: "coachingTracking",
    items: [
      {
        to: "/admin/coaching-flow",
        text: "Coaching flow",
        icon: "coachingTracking",
      },
      {
        to: "/admin/student-roadmap-overview",
        text: "Studen Roadmap",
        icon: "studentRoadMapOverview",
      },
      {
        to: "/admin/coaching-flow-management",
        text: "Roadmap management",
        icon: "studentRoadMapOverview",
      },
    ],
  },
];

const SalesSideBar = [
  { to: "/sales/home", text: "Home", icon: "home" },
  { to: "/sales/track-lead", text: "Track Lead", icon: "trackUser" },
  { to: "/sales/kanban", text: "Kanban", icon: "kanban" },
  { to: "/sales/user-tracking", text: "Track User", icon: "trackUser" },
  { to: "/sales/deposits", text: "Deposits", icon: "finance" },
  {
    to: "/sales/checkout-discount-links",
    text: "Checkout discount",
    icon: "signupLink",
  },
  {
    to: "/sales/paypal-discount-links",
    text: "Paypal Discount links",
    icon: "signupLink",
  },
];

const managerSideBar = [
  { to: "/admin/track-lead", text: "Track Lead", icon: "trackUser" },
  { to: "/admin/deposits", text: "Deposits", icon: "finance" },
];

const SPRING = { type: "spring", stiffness: 320, damping: 24 };

// Shared nav link (user items, single admin items, group items)
const NavItem = ({ to, text, icon, small = false, renderIcon }) => (
  <motion.div whileHover={{ x: 3 }} whileTap={{ scale: 0.97 }} transition={SPRING}>
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 text-white text-left rounded-lg transition-all duration-300 ${
          small ? "text-sm py-2 px-3" : "text-sm py-2 px-4"
        } ${
          isActive
            ? "bg-gradient-to-r from-[#113be1] via-[#4268ff] to-[#113be1] shadow-[0_8px_24px_-10px_#4268ff]"
            : "hover:bg-white/5"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex items-center justify-center rounded-md transition-all duration-300 group-hover:scale-105 ${
              small ? "w-6 h-6" : "w-8 h-8"
            } ${
              isActive
                ? "bg-white"
                : `${small ? "bg-[#1a1a1a]" : "bg-[#000]"} group-hover:ring-1 group-hover:ring-[#4268ff]/60`
            }`}
          >
            {renderIcon ? (
              renderIcon(isActive)
            ) : (
              <SvgIcon name={icon} isActive={isActive} size={small ? "sm" : undefined} />
            )}
          </span>
          <span
            className={`flex items-center text-left ${small ? "text-xs" : ""} ${
              isActive ? "font-semibold" : "font-normal"
            }`}
          >
            {text}
          </span>
        </>
      )}
    </NavLink>
  </motion.div>
);

// Collapsible Group Component
const CollapsibleGroup = ({ group, currentPath, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(() => {
    // Auto-open group if current path matches any item in the group
    return group.items.some((item) => currentPath.startsWith(item.to));
  });

  const toggleGroup = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="mb-2">
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={toggleGroup}
        aria-expanded={isOpen}
        className={`group flex items-center justify-between w-full text-white text-left text-sm py-2 px-4 rounded-lg transition-all duration-300 hover:bg-white/5 ${
          isOpen ? "bg-white/5" : ""
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-md bg-[#000] transition-all duration-300 group-hover:scale-105 ${
              isOpen ? "ring-1 ring-[#4268ff] shadow-[0_0_14px_-3px_#4268ff]" : ""
            }`}
          >
            <SvgIcon name={group.icon} />
          </span>
          <span className="font-medium">{group.title}</span>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="flex items-center justify-center w-5 h-5"
        >
          <ChevronDown size={18} />
        </motion.span>
      </motion.button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="group-items"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="ml-6 mt-1 pl-3 border-l border-[rgba(144,157,172,0.3)]">
              {group.items.map(({ to, text, icon }, i) => (
                <motion.div
                  key={to + i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.25 }}
                  className="my-1"
                  onClick={onNavigate}
                >
                  <NavItem to={to} text={text} icon={icon} small />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Navbar({ setIsSidebarOpen }) {
  const [navItems, setNavItems] = useState(null);
  const [navGroups, setNavGroups] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const navigate = useNavigate();
  const { user, score, pseudo, sessionsBookingLocked, loading: profileLoading } =
    useUserRole();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      if (user) {
        try {
          const tokenResult = await getIdTokenResult(user);
          const claims = tokenResult.claims;
          if (claims?.isAdmin) {
            if (claims.role === "admin") {
              setNavGroups(adminSidebarGroups);
              setNavItems(null);
            } else if (claims.role === "manager") {
              setNavItems(managerSideBar);
              setNavGroups(null);
            } else {
              setNavItems(SalesSideBar);
              setNavGroups(null);
            }
          } else {
            setNavItems(userSideBar);
            setNavGroups(null);
          }
        } catch (error) {
          console.error("Error getting token claims:", error);
        }
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Update current path when location changes
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [navigate]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        navigate("/login");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const handleNavigate = () => {
    if (window.innerWidth < 900) {
      setIsSidebarOpen(false);
    }
  };

  if (isLoading) {
    return <SidebarSkeleton />;
  }

  return (
    <div className="flex flex-col h-[100%] min-h-[425px] w-[270px] transition-all duration-1000 border-r border-[rgba(144,157,172,0.2)] mdPlus:relative bg-[#0F28A4] md:bg-transparent fixed left-0 top-0 z-20 overflow-hidden">
      {/* subtle ambient glow */}
      <div className="pointer-events-none absolute -top-28 -left-28 h-60 w-60 rounded-full bg-[#4268ff] opacity-15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-28 h-60 w-60 rounded-full bg-[#113be1] opacity-15 blur-3xl" />

      {/* logo */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative p-3 border-b-[1px]"
        style={{
          borderImage:
            "linear-gradient(to right, rgba(144, 157, 172, 0), rgba(144, 157, 172, 1), rgba(144, 157, 172, 0)) 1",
        }}
      >
        <div className="flex items-center gap-3 p-2">
          <motion.span whileHover={{ scale: 1.03 }} transition={SPRING}>
            <img src={LogoImg} width={180} alt="logo" />
          </motion.span>
        </div>
      </motion.div>

      <div className="relative flex-grow overflow-y-auto h-fit scrollbar-thin scrollbar-thumb-[#4268ff] scrollbar-track-[#113be1]/0 scrollbar-thumb-rounded-full scrollbar-corner-[#113be1]/0">
        <div className="p-3">
          <div className="user-info mb-4 rounded-xl bg-white/5 border border-white/10 transition-colors duration-300 hover:bg-white/10">
            <NavUserInfo user={user} pseudo={pseudo} score={score} />
          </div>

          {/* <p className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A0AEC0]">
            Menu
          </p> */}

          <nav className="NavLinks">
            <ul className="p-0">
              {/* Render grouped navigation for admin */}
              {navGroups?.map((group, index) => (
                <li key={index}>
                  {group?.isSingle ? (
                    <div className="mb-2" onClick={handleNavigate}>
                      <NavItem
                        to={group.to}
                        text={group.title}
                        renderIcon={(isActive) => (
                          <Video size={16} color={isActive ? "#0075ff" : "white"} />
                        )}
                      />
                    </div>
                  ) : (
                    <CollapsibleGroup
                      group={group}
                      currentPath={currentPath}
                      onNavigate={handleNavigate}
                    />
                  )}
                </li>
              ))}

              {/* Render regular navigation for non-admin users */}
              {navItems?.map(({ to, text, icon, requiresOneToOne }, i) => {
                const isBookingLocked =
                  requiresOneToOne && !profileLoading && sessionsBookingLocked;
                const isBookingPending = requiresOneToOne && profileLoading;

                if (isBookingPending) {
                  return (
                    <li key={to} className="my-2">
                      <div
                        className="flex items-center gap-2.5 text-white/35 text-left text-sm py-2 px-4 rounded-lg animate-pulse cursor-wait"
                        aria-busy="true"
                      >
                        <span className="flex items-center justify-center w-8 h-8 rounded-md bg-[#000]/80" />
                        <span className="h-4 flex-1 max-w-[140px] rounded bg-white/10" />
                      </div>
                    </li>
                  );
                }

                if (isBookingLocked) {
                  return (
                    <li key={to} className="my-2">
                      <div
                        className="flex items-center gap-2.5 text-white/45 text-left text-sm py-2 px-4 rounded-lg cursor-not-allowed select-none border border-dashed border-white/10"
                        title="Disponible avec l’accompagnement 1:1"
                        aria-disabled="true"
                      >
                        <span className="flex items-center justify-center w-8 h-8 rounded-md bg-[#000] opacity-70">
                          <SvgIcon name={icon} isActive={false} />
                        </span>
                        <span className="flex items-center gap-2 text-left min-w-0 flex-1">
                          <span className="truncate">{text}</span>
                          <Lock
                            className="w-4 h-4 shrink-0 text-white/40"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </li>
                  );
                }

                return (
                  <motion.li
                    key={to}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.35 }}
                    className="my-2"
                    onClick={handleNavigate}
                  >
                    <NavItem to={to} text={text} icon={icon} />
                  </motion.li>
                );
              })}

              <li
                className="my-2 mt-4 pt-3 border-t border-[rgba(144,157,172,0.2)]"
                onClick={() => handleLogout()}
              >
                <motion.div
                  whileHover={{ x: 3 }}
                  whileTap={{ scale: 0.97 }}
                  transition={SPRING}
                >
                  <NavLink
                    to={""}
                    className="group flex items-center gap-2.5 text-white text-sm py-2 px-4 rounded-lg transition-all duration-300 hover:bg-white/5"
                  >
                    <span className="flex items-center justify-center w-8 h-8 rounded-md bg-[#000] transition-all duration-300 group-hover:scale-105 group-hover:ring-1 group-hover:ring-[#4268ff]/60">
                      <SvgIcon name={"logout"} />
                    </span>
                    Déconnexion
                  </NavLink>
                </motion.div>
              </li>
              <li></li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="relative m-10 ms-5 w-full border-[rgba(144,157,172,1)]">
        <DocumentationCard />
      </div>
    </div>
  );
}